import { useMutation } from '@apollo/client';
import { documentTypesLocales as libDocumentTypesLocales } from '@shinetools/shine-start-library';
import LocalizedStrings from 'localized-strings';

import { CompanyCreationDocument } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import Header from 'features/Onboarding/components/Header';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import OBStickyButton from 'features/Onboarding/components/OBStickyButton/OBStickyButton';
import DisplayOrUploadDoc from 'features/Onboarding/legacy/components/DisplayOrUploadDoc';
import { getUploadKybFunction } from 'features/Storage/upload';

import locales from '../../../ShineStartVerification/pages/HandwrittenIdentityUpload/locales';
import { UploadSectionWrapper } from '../../../ShineStartVerification/pages/HandwrittenIdentityUpload/styles';
import { DeleteCompanyCreationDocumentDocument } from '../../graphql/mutations/delete-company-creation-document.gql';
import { ProvideCompanyCreationDocumentDocument } from '../../graphql/mutations/provide-company-creation-document.gql';
import { VerificationModuleDataDocument } from '../../graphql/queries/verificationModuleData.gql';

const documentTypesLocales = new LocalizedStrings(libDocumentTypesLocales);

interface HandwrittenIdentityUploadProps {
  onNext: () => void;
  document: CompanyCreationDocument;
}
const HandwrittenIdentityUpload = asBentoPage<HandwrittenIdentityUploadProps>(
  ({ document, Layout, onNext, onPrev }) => {
    const [addDocument, { loading: isAddingDocument }] = useMutation(
      ProvideCompanyCreationDocumentDocument,
      {
        awaitRefetchQueries: true,
        refetchQueries: [VerificationModuleDataDocument],
      },
    );
    const [deleteDocument, { loading: isDeletingDocument }] = useMutation(
      DeleteCompanyCreationDocumentDocument,
      {
        awaitRefetchQueries: true,
        refetchQueries: [VerificationModuleDataDocument],
      },
    );

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} paddingBottom="space-24" />}>
        <Header description={locales.description} title={locales.title} />

        <UploadSectionWrapper>
          <DisplayOrUploadDoc
            existingDocumentUrl={document.file?.url}
            isDeletingExistingDocument={isDeletingDocument}
            locales={{
              uploadedDocumentName:
                documentTypesLocales.PROOF_OF_IDENTITY_HAND_WRITTEN.label,
              uploadFailureMessage: locales.uploadFailureMessage,
              uploadFormBrowse: locales.uploadFormBrowse,
              uploadFormDragDrop: locales.uploadFormDragDrop,
            }}
            onDeleteExistingDocument={async () => {
              await deleteDocument({
                variables: {
                  input: {
                    applicationId: document.applicationId,
                    documentId: document.id,
                  },
                },
              });
            }}
            onSuccessfulUpload={async ({ bucketName, fileRef }) => {
              await addDocument({
                variables: {
                  input: {
                    applicationId: document.applicationId,
                    fileBucketName: bucketName,
                    filePath: fileRef,
                    type: document.type,
                  },
                },
              });
            }}
            uploadFile={getUploadKybFunction(document.applicationId)}
          />
        </UploadSectionWrapper>

        <OBStickyButton
          isDisabled={!document.file || isAddingDocument || isDeletingDocument}
          onClick={onNext}
        >
          {locales.ctaContinue}
        </OBStickyButton>
      </Layout>
    );
  },
);

export default HandwrittenIdentityUpload;
